import { Controller as StimulusController } from "@hotwired/stimulus"
import { debug, eventDescriptors, handleEvent, rootName } from "web-ui/controller/event-descriptors"

// Stimulus reserves 13 member names on Controller, plus every generated
// xTarget, xValue, xClass, and xOutlet, and a migrated controller shares that
// namespace. The listeners this class installs are held here rather than on the
// controller so that the class adds only startEventHandling and
// stopEventHandling to it. - Aaron, Sat Aug 22 2026
const installedListeners = new WeakMap()

export class Controller extends StimulusController {
  constructor(context) {
    super(context)

    // Wrapping connect and disconnect here rather than defining them as methods
    // means a subclass never calls super.connect. A subclass cannot skip this
    // constructor, the prototype chain is complete by the time it runs, and the
    // instance properties assigned below shadow the subclass's prototype
    // methods. - Aaron, Sat Aug 22 2026
    const connect = this.connect.bind(this)
    const disconnect = this.disconnect.bind(this)

    this.connect = () => {
      this.startEventHandling()

      connect()
    }

    this.disconnect = () => {
      disconnect()

      this.stopEventHandling()
    }
  }

  startEventHandling() {
    const descriptors = eventDescriptors(this)

    const listeners = []

    // One native listener per descriptor, with no sharing between descriptors.
    // Stimulus shares a listener between the actions that want the same event,
    // and that pays off because one application-wide Dispatcher shares the
    // window and document listeners of every controller on the page. This class
    // installs per controller instance, so sharing could only ever combine two
    // descriptors of one controller that name the same root, event, and
    // options. Separate listeners also get stopImmediatePropagation from the
    // browser; a shared listener would have to reproduce that suppression the
    // way EventListener#handleEvent does. - Aaron, Sun Aug 23 2026
    for (const descriptor of descriptors) {
      const listener = (event) => handleEvent(this, event, descriptor)

      descriptor.root.addEventListener(descriptor.event, listener, descriptor.options)

      listeners.push({ root: descriptor.root, event: descriptor.event, listener, options: descriptor.options })

      debug("Installed listener", {
        identifier: this.identifier,
        root: rootName(descriptor.root),
        event: descriptor.event,
        options: descriptor.options,
        handler: descriptor.value
      })
    }

    installedListeners.set(this, listeners)
  }

  stopEventHandling() {
    const listeners = installedListeners.get(this) || []

    for (const { root, event, listener, options } of listeners) {
      root.removeEventListener(event, listener, options)

      debug("Removed listener", { identifier: this.identifier, root: rootName(root), event })
    }

    installedListeners.delete(this)
  }
}
